import { useEffect, useRef, useState } from 'react';
import useScript from 'hooks/useScript';
import { useLocation } from 'react-router-dom';

/**
 * UsercentricsConsent
 *
 * Loads the Usercentrics consent script on allowed paths, with delayed and asynchronous loading.
 * - Only runs on client side (SSR safe)
 * - Configurable allowedPaths
 * - Handles script load errors and toggles consent UI
 *
 * @param allowedPaths - Array of pathnames where consent should be loaded (default: ['/'])
 */
type UsercentricsConsentProps = {
	allowedPaths?: string[];
};

const UsercentricsConsent = ({ allowedPaths = ['/'] }: UsercentricsConsentProps) => {
	// SSR safety: only run on client
	if (typeof window === 'undefined') return null;

	const location = useLocation();
	const usercentricsRootRef = useRef<HTMLElement | null>(null);
	const [loadScript, setLoadScript] = useState(false);
	const [scriptLoadedSuccessfully, setScriptLoadedSuccessfully] = useState(false);

	const shouldLoadScript = allowedPaths.includes(location.pathname);

	// Delay script loading by 10 seconds
	useEffect(() => {
		if (shouldLoadScript && !loadScript) {
			const timer = setTimeout(() => {
				setLoadScript(true);
			}, 10000);
			return () => clearTimeout(timer);
		}
	}, [shouldLoadScript, loadScript]);

	// Load Usercentrics script
	const scriptStatus = useScript(
		shouldLoadScript && loadScript
			? 'https://app.eu.usercentrics.eu/browser-ui/latest/loader.js'
			: '' /* pass empty string if not loading */,
		{
			id: 'usercentrics-cmp',
			async: 'true',
			'data-eu-mode': 'true',
			'data-settings-id': 'DdWpKF2JGG6ynk',
		},
	);

	// Show consent only if script loaded successfully
	useEffect(() => {
		if (scriptStatus === 'ready') {
			setScriptLoadedSuccessfully(true);
		} else if (scriptStatus === 'error') {
			// eslint-disable-next-line no-console
			console.error('❌ Usercentrics script failed to load');
			setScriptLoadedSuccessfully(false);
		}
	}, [scriptStatus]);

	useEffect(() => {
		const timer = setTimeout(() => {
			if (scriptLoadedSuccessfully) {
				usercentricsRootRef.current = document.getElementById(
					'usercentrics-root',
				) as HTMLElement | null;
				if (usercentricsRootRef.current) {
					usercentricsRootRef.current.style.display = 'block';
				}
			}
		}, 10000);

		return () => {
			clearTimeout(timer);
			const usercentricsRoot = document.getElementById('usercentrics-root');
			if (usercentricsRoot) {
				usercentricsRoot.style.display = 'none';
			}
		};
	}, [scriptLoadedSuccessfully]);

	return null;
};

export default UsercentricsConsent;
